import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useLocation, useNavigate } from 'react-router';
import styled from 'styled-components';

import { catchAll } from '@sorare/routing';

import { ButtonBase } from '#src/atoms/buttons/ButtonBase/index.tsx';
import { ProIcon } from '#src/atoms/icons/Pro.tsx';
import { Horizontal } from '#src/atoms/layout/flex/index.tsx';
import { LabelL } from '#src/atoms/typography/index.tsx';
import { Badge } from '#src/atoms/ui/Badge/index.tsx';
import {
  ANY_SPORT_HOME,
  ANY_SPORT_MARKET,
  ANY_SPORT_MY_CARDS,
  ANY_SPORT_PLAY,
  ANY_SPORT_SCORES,
  ANY_SPORT_SERIES_MARKET,
  ANY_SPORT_SERIES_PLAY,
} from '#src/constants/__generated__/routes.ts';
import { useSportContext } from '#src/contexts/sport/index.ts';
import { useEnableSetFeatures } from '#src/hooks/commonGame/useEnableSetFeatures.ts';
import { useSeriesNav } from '#src/hooks/commonGame/useSeriesNav.ts';
import { useIsTabletAndAbove } from '#src/hooks/device/useIsTabletAndAbove.ts';
import { useLockProMode } from '#src/hooks/useLockProMode.ts';
import { useUnlockProMode } from '#src/hooks/useUnlockProMode.ts';
import { useUnclaimedMarketTasksCount } from '#src/hooks/users/useUnclaimedMarketTasksCount.ts';
import { matchAnySportPath } from '#src/lib/routing/generateSportPath.ts';
import { useCommonGameSet } from '#src/set/index.ts';
import { tabletAndAbove } from '#src/style/mediaQuery.ts';

const Root = styled(Horizontal)`
  width: 100%;
  border-radius: var(--unit);
  background: var(--c-nd-50);
  padding: 2px;
  align-self: center;

  & > * {
    flex: 1;
  }

  &.collapsed {
    flex-direction: column;
  }

  @media ${tabletAndAbove} {
    padding: var(--half-unit);
  }
`;
const ToggleItem = styled(Horizontal).attrs({ center: true })`
  border-radius: calc(var(--unit) - 2px);
  padding: var(--half-unit) var(--unit);

  &.selected {
    background: var(--c-black);
  }

  @media ${tabletAndAbove} {
    padding: var(--unit);
  }
`;
const InlineSetBadge = styled(Badge)`
  @media ${tabletAndAbove} {
    margin-left: var(--half-unit);
  }
`;

type Props = {
  collapsed?: boolean;
};

const SetToProPath = {
  [ANY_SPORT_SERIES_PLAY]: ANY_SPORT_PLAY,
  [ANY_SPORT_SERIES_MARKET]: ANY_SPORT_MARKET,
  [ANY_SPORT_MY_CARDS]: ANY_SPORT_MY_CARDS,
};

const ProToSetPath = {
  [ANY_SPORT_PLAY]: ANY_SPORT_SERIES_PLAY,
  [ANY_SPORT_HOME]: ANY_SPORT_SERIES_PLAY,
  [ANY_SPORT_SCORES]: ANY_SPORT_SERIES_PLAY,
  [ANY_SPORT_MY_CARDS]: ANY_SPORT_MY_CARDS,
  [ANY_SPORT_MARKET]: ANY_SPORT_SERIES_MARKET,
};

export const ProSetToggle = ({ collapsed }: Props) => {
  const { SetIcon } = useCommonGameSet().visual.brand;
  const enableSetFeatures = useEnableSetFeatures();
  const { sport, generateSportContextPath } = useSportContext();
  const navigate = useNavigate();
  const seriesNav = useSeriesNav();
  const { unlockProMode } = useUnlockProMode();
  const { lockProMode } = useLockProMode();
  const { pathname } = useLocation();
  const isTabletAndAbove = useIsTabletAndAbove();
  const unclaimedMarketTasksCount = useUnclaimedMarketTasksCount();
  const shouldDisplaySetLabel = !collapsed && isTabletAndAbove;

  if (!enableSetFeatures) {
    return null;
  }

  return (
    <Root className={classNames({ collapsed })}>
      <ButtonBase
        onClick={() => {
          if (!enableSetFeatures) {
            return;
          }
          lockProMode({
            variables: {
              sport,
              input: {
                sport,
                interactionContext: 'user-nav',
              },
            },
          });
          let navigated = false;
          for (const key of Object.keys(ProToSetPath)) {
            if (matchAnySportPath(catchAll(key), pathname)) {
              navigate(
                generateSportContextPath(
                  ProToSetPath[key as keyof typeof ProToSetPath]
                )
              );
              navigated = true;
              break;
            }
          }
          if (!navigated) {
            // Fallback: Navigate to SERIES_PLAY if never matching
            navigate(generateSportContextPath(ANY_SPORT_SERIES_PLAY));
          }
        }}
      >
        <ToggleItem className={classNames({ selected: seriesNav })}>
          {shouldDisplaySetLabel ? (
            <SetIcon />
          ) : (
            <Badge size="small" badgeContent={unclaimedMarketTasksCount}>
              <SetIcon />
            </Badge>
          )}
          {!!shouldDisplaySetLabel && (
            <LabelL>
              <FormattedMessage defaultMessage="Set" />
            </LabelL>
          )}
          {!!shouldDisplaySetLabel && (
            <InlineSetBadge
              size="small"
              badgeContent={unclaimedMarketTasksCount}
            />
          )}
        </ToggleItem>
      </ButtonBase>
      <ButtonBase
        onClick={() => {
          unlockProMode({
            variables: {
              sport,
              input: {
                sport,
                interactionContext: 'user-nav',
              },
            },
          });
          let navigated = false;
          for (const key of Object.keys(SetToProPath)) {
            if (matchAnySportPath(catchAll(key), pathname)) {
              navigate(
                generateSportContextPath(
                  SetToProPath[key as keyof typeof SetToProPath]
                )
              );
              navigated = true;
              break;
            }
          }
          if (!navigated) {
            // Fallback: Navigate to ANY_SPORT_HOME if never matching
            navigate(generateSportContextPath(ANY_SPORT_HOME, { sport }));
          }
        }}
      >
        <ToggleItem className={classNames({ selected: !seriesNav })}>
          <ProIcon />
          {!collapsed && !!isTabletAndAbove && (
            <LabelL>
              <FormattedMessage defaultMessage="Pro" />
            </LabelL>
          )}
        </ToggleItem>
      </ButtonBase>
    </Root>
  );
};
